/**
 * Mouse device (§3.1, §3.3). Focus Aim on RMB with the aim vector = player → cursor, and UI
 * confirm on LMB.
 *
 * §3.8: aim assist on mouse is a targeting bias only. This module never calls `requestPointerLock`,
 * never writes to the cursor and exposes `cursorDisplacements` (always 0) so the acceptance test
 * can assert "zero cursor-displacement events logged on mouse input".
 */

import { ACTIONS, BindingTable, type ActionId } from './bindings';

export class MouseDevice {
  heldMask = 0;
  pressedMask = 0;
  readonly pressTick = new Int32Array(ACTIONS.length).fill(-1);
  activity = false;
  /** Cursor position in CSS px relative to the game surface's top-left. */
  x = 0;
  y = 0;
  inside = false;
  /** §3.8 acceptance: this counter is never incremented. */
  readonly cursorDisplacements = 0;

  private buttons = 0;
  private tick = 0;
  private revision = -1;
  private disposed = false;
  private rectLeft = 0;
  private rectTop = 0;

  private readonly onDown: (ev: PointerEvent) => void;
  private readonly onUp: (ev: PointerEvent) => void;
  private readonly onMove: (ev: PointerEvent) => void;
  private readonly onLeave: (ev: PointerEvent) => void;
  private readonly onContextMenu: (ev: Event) => void;
  private readonly onBlur: () => void;

  constructor(
    private target: HTMLElement,
    private bindings: BindingTable,
  ) {
    this.onDown = (ev) => this.handleDown(ev);
    this.onUp = (ev) => this.handleUp(ev);
    this.onMove = (ev) => this.handleMove(ev);
    this.onLeave = (ev) => {
      if (ev.pointerType === 'mouse') this.inside = false;
    };
    this.onContextMenu = (ev) => ev.preventDefault();
    this.onBlur = () => this.releaseAll();

    target.addEventListener('pointerdown', this.onDown, { passive: false });
    target.addEventListener('pointermove', this.onMove, { passive: true });
    target.addEventListener('pointerleave', this.onLeave, { passive: true });
    target.addEventListener('contextmenu', this.onContextMenu);
    if (typeof window !== 'undefined') {
      window.addEventListener('pointerup', this.onUp, { passive: false });
      window.addEventListener('blur', this.onBlur);
    }
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', this.onBlur);
  }

  /**
   * Raw LMB state, independent of the binding table. Pointer-follow reads this rather than an
   * action bit: LMB carries `ui_confirm`, which the player may rebind, and the follow must not
   * move with it.
   */
  get leftDown(): boolean {
    return (this.buttons & 1) !== 0;
  }

  setTick(tick: number): void {
    this.tick = tick;
    if (this.revision !== this.bindings.revision) {
      this.revision = this.bindings.revision;
      this.recomputeHeld();
    }
  }

  drainPressed(): number {
    const m = this.pressedMask;
    this.pressedMask = 0;
    return m;
  }

  isHeld(action: ActionId): boolean {
    return (this.heldMask & (1 << ACTIONS.indexOf(action))) !== 0;
  }

  releaseAll(): void {
    this.buttons = 0;
    this.heldMask = 0;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    const t = this.target;
    t.removeEventListener('pointerdown', this.onDown);
    t.removeEventListener('pointermove', this.onMove);
    t.removeEventListener('pointerleave', this.onLeave);
    t.removeEventListener('contextmenu', this.onContextMenu);
    if (typeof window !== 'undefined') {
      window.removeEventListener('pointerup', this.onUp);
      window.removeEventListener('blur', this.onBlur);
    }
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', this.onBlur);
    this.releaseAll();
  }

  /** Test/headless hook: press a button at a surface-relative CSS px point. */
  injectDown(button: number, x: number, y: number): void {
    this.applyDown(button, x, y);
  }

  /** Test/headless hook: move the cursor without a DOM event. */
  injectMove(x: number, y: number): void {
    this.x = x;
    this.y = y;
    this.inside = true;
    this.activity = true;
  }

  /** Test/headless hook: release a button without a DOM event. */
  injectUp(button: number): void {
    this.applyUp(button);
  }

  private handleDown(ev: PointerEvent): void {
    if (ev.pointerType !== 'mouse') return;
    this.refreshRect();
    if (this.bindings.actionsFor('mouse', 'm:' + ev.button) !== 0) ev.preventDefault();
    this.applyDown(ev.button, ev.clientX - this.rectLeft, ev.clientY - this.rectTop);
  }

  private handleUp(ev: PointerEvent): void {
    if (ev.pointerType !== 'mouse') return;
    this.applyUp(ev.button);
  }

  private handleMove(ev: PointerEvent): void {
    if (ev.pointerType !== 'mouse') return;
    this.refreshRect();
    this.x = ev.clientX - this.rectLeft;
    this.y = ev.clientY - this.rectTop;
    this.inside = true;
    if (ev.movementX !== 0 || ev.movementY !== 0) this.activity = true;
  }

  private applyDown(button: number, x: number, y: number): void {
    const bit = 1 << button;
    if (this.buttons & bit) return;
    this.buttons |= bit;
    this.activity = true;
    this.x = x;
    this.y = y;
    this.inside = true;
    const mask = this.bindings.actionsFor('mouse', 'm:' + button);
    this.heldMask |= mask;
    this.pressedMask |= mask;
    for (let i = 0; i < ACTIONS.length; i++) {
      if (mask & (1 << i)) this.pressTick[i] = this.tick;
    }
  }

  private applyUp(button: number): void {
    const bit = 1 << button;
    if (!(this.buttons & bit)) return;
    this.buttons &= ~bit;
    this.recomputeHeld();
  }

  private refreshRect(): void {
    const r = this.target.getBoundingClientRect();
    this.rectLeft = r.left;
    this.rectTop = r.top;
  }

  private recomputeHeld(): void {
    let m = 0;
    for (let b = 0; b < 5; b++) {
      if (this.buttons & (1 << b)) m |= this.bindings.actionsFor('mouse', 'm:' + b);
    }
    this.heldMask = m;
  }
}
